import { useEffect, useState, useCallback } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import { useAuth } from "@/shared/lib/auth";
import FileUpload from "@/shared/components/FileUpload";
import {
  FilePlus2, PackageMinus, PackagePlus, Mail, X, Check, XCircle, Clock,
} from "lucide-react";
import { toast } from "sonner";

/* Scope amendments (CSM): Add scope / De-scope / Re-scope — each backed by the
   client's email. De-scope & Re-scope go through Management/Scope-user approval. */

const EMAIL_ACCEPT = ".eml,.pdf,.png,.jpg,.jpeg,.webp,.doc,.docx";
const UNITS = ["sqft", "sqm", "rft", "rmt", "nos", "sheet", "set", "lumpsum"];

export function ScopeChangeActions({ code, lines, onDone }) {
  const [modal, setModal] = useState(null); // add | descope | rescope
  const close = (changed) => { setModal(null); if (changed) onDone?.(); };
  return (
    <div className="flex items-center gap-2" data-testid="sc-actions">
      <ActionBtn icon={FilePlus2} label="Add scope" tone="emerald" testid="sc-add-btn" onClick={() => setModal("add")} />
      <ActionBtn icon={PackageMinus} label="De-scope" tone="rose" testid="sc-descope-btn" onClick={() => setModal("descope")} />
      <ActionBtn icon={PackagePlus} label="Re-scope" tone="sky" testid="sc-rescope-btn" onClick={() => setModal("rescope")} />
      {modal === "add" && <AddScopeModal code={code} onClose={close} />}
      {modal === "descope" && <ChangeModal type="descope" code={code} lines={lines.filter((l) => !l.descoped)} onClose={close} />}
      {modal === "rescope" && <ChangeModal type="rescope" code={code} lines={lines} onClose={close} />}
    </div>
  );
}

function ActionBtn({ icon: Icon, label, tone, testid, onClick }) {
  const tones = {
    emerald: "border-emerald-300 text-emerald-700 hover:bg-emerald-50",
    rose: "border-rose-300 text-rose-700 hover:bg-rose-50",
    sky: "border-sky-300 text-sky-700 hover:bg-sky-50",
  };
  return (
    <button onClick={onClick} data-testid={testid}
            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full border bg-white text-xs font-semibold transition-colors ${tones[tone]}`}>
      <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {label}
    </button>
  );
}

function Modal({ kicker, title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-[2px] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-start justify-between px-5 pt-5">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-orange-600 font-semibold">{kicker}</div>
            <h3 className="font-display text-lg font-bold tracking-tight mt-0.5">{title}</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 mt-1" data-testid="sc-modal-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="px-5 pb-5 pt-4 space-y-3">{children}</div>
      </div>
    </div>
  );
}

const inputCls = "w-full h-9 px-3 rounded-md border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-orange-300";
const labelCls = "block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1";

function EmailField({ value, onChange }) {
  return (
    <div>
      <label className={labelCls}><Mail className="inline w-3 h-3 mr-1 -mt-0.5" />Client email (required)</label>
      <FileUpload value={value} onChange={onChange} label="Attach client email (.eml / .pdf / screenshot)"
                  accept={EMAIL_ACCEPT} testid="sc-email-upload" />
    </div>
  );
}

function SubmitBtn({ disabled, busy, label, testid, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled || busy} data-testid={testid}
            className="w-full h-10 rounded-lg bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 transition-colors">
      {busy ? "Submitting…" : label}
    </button>
  );
}

function AddScopeModal({ code, onClose }) {
  const [f, setF] = useState({ title: "", description: "", unit: "sqft", quantity: "", email_url: "", note: "" });
  const [busy, setBusy] = useState(false);
  const ok = f.title.trim() && f.email_url && Number(f.quantity) > 0;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${code}/scope-lines`, { ...f, quantity: Number(f.quantity) });
      toast.success("Scope added — Management & Scope user notified for commercials + amended PO");
      onClose(true);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal kicker="Scope amendment" title="Add a new scope" onClose={() => onClose(false)} testid="sc-add-modal">
      <div>
        <label className={labelCls}>Scope description</label>
        <input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })}
               placeholder="e.g. Reception backlit signage" data-testid="sc-add-title" />
      </div>
      <div>
        <label className={labelCls}>Details (optional)</label>
        <input className={inputCls} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
               placeholder="Spec / notes" data-testid="sc-add-desc" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Quantity</label>
          <input type="number" min="0" className={inputCls} value={f.quantity}
                 onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="sc-add-qty" />
        </div>
        <div>
          <label className={labelCls}>Unit</label>
          <select className={inputCls} value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} data-testid="sc-add-unit">
            {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
      </div>
      <EmailField value={f.email_url} onChange={(url) => setF({ ...f, email_url: url })} />
      <p className="text-[11px] text-zinc-500">Goes live immediately. Management & the Scope user are notified to enter the commercials and the amended PO.</p>
      <SubmitBtn disabled={!ok} busy={busy} label="Add scope" testid="sc-add-submit" onClick={submit} />
    </Modal>
  );
}

function ChangeModal({ type, code, lines, onClose }) {
  const descope = type === "descope";
  const [f, setF] = useState({ boq_id: "", full: true, qty_change: "", email_url: "", reason: "" });
  const [busy, setBusy] = useState(false);
  const line = lines.find((l) => l.boq_id === f.boq_id);
  const ok = f.boq_id && f.email_url && (descope ? (f.full || Number(f.qty_change) > 0) : Number(f.qty_change) > 0);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${code}/scope-changes`, {
        type, boq_id: f.boq_id, full: descope ? f.full : false,
        qty_change: Number(f.qty_change) || 0, email_url: f.email_url, reason: f.reason,
      });
      toast.success(`${descope ? "De-scope" : "Re-scope"} request sent for approval`);
      onClose(true);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal kicker="Scope amendment" title={descope ? "De-scope an item" : "Re-scope (add quantity)"}
           onClose={() => onClose(false)} testid={`sc-${type}-modal`}>
      <div>
        <label className={labelCls}>Scope line</label>
        <select className={inputCls} value={f.boq_id} onChange={(e) => setF({ ...f, boq_id: e.target.value })}
                data-testid={`sc-${type}-line`}>
          <option value="">Select scope line…</option>
          {lines.map((l) => (
            <option key={l.boq_id} value={l.boq_id}>
              {l.title}{l.quantity ? ` — ${l.quantity} ${l.unit}` : ""}{l.descoped ? " (descoped)" : ""}
            </option>
          ))}
        </select>
      </div>
      {descope && (
        <div className="flex rounded-lg border border-zinc-200 overflow-hidden text-xs font-semibold">
          <button onClick={() => setF({ ...f, full: true })} data-testid="sc-descope-full"
                  className={`flex-1 h-8 ${f.full ? "bg-rose-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Entire scope
          </button>
          <button onClick={() => setF({ ...f, full: false })} data-testid="sc-descope-partial"
                  className={`flex-1 h-8 ${!f.full ? "bg-rose-600 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"}`}>
            Reduce quantity
          </button>
        </div>
      )}
      {(!descope || !f.full) && (
        <div>
          <label className={labelCls}>{descope ? "Quantity to remove" : "Quantity to add"}{line ? ` (current: ${line.quantity} ${line.unit})` : ""}</label>
          <input type="number" min="0" className={inputCls} value={f.qty_change}
                 onChange={(e) => setF({ ...f, qty_change: e.target.value })} data-testid={`sc-${type}-qty`} />
        </div>
      )}
      <EmailField value={f.email_url} onChange={(url) => setF({ ...f, email_url: url })} />
      <div>
        <label className={labelCls}>Reason (optional)</label>
        <input className={inputCls} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}
               data-testid={`sc-${type}-reason`} />
      </div>
      <p className="text-[11px] text-zinc-500">Nothing changes until Management or the Scope user approves this request.</p>
      <SubmitBtn disabled={!ok} busy={busy} label={descope ? "Request de-scope" : "Request re-scope"}
                 testid={`sc-${type}-submit`} onClick={submit} />
    </Modal>
  );
}

/* Requests list — pending first, approvable by admin / management / scope_user.
   Reused in Sub-scope (project-filtered) and on the Dashboard. */
export function ScopeChangeRequests({ code, onChanged, title = "Scope change requests" }) {
  const { user } = useAuth();
  const [reqs, setReqs] = useState(null);
  const [busyId, setBusyId] = useState("");
  const canDecide = ["admin", "management", "scope_user"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/scope-changes").then((r) => {
      let rows = r.data.requests || [];
      if (code) rows = rows.filter((x) => x.project_code === code);
      const pending = rows.filter((x) => x.status === "pending");
      const decided = rows.filter((x) => x.status !== "pending").slice(0, 5);
      setReqs([...pending, ...decided]);
    }).catch(() => setReqs([]));
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const decide = async (rid, action) => {
    setBusyId(rid);
    try {
      const { data } = await api.post(`/scope-changes/${rid}/decide`, { action, note: "" });
      toast.success(action === "approve" ? `Approved — ${data.applied || "done"}` : "Request rejected");
      load(); onChanged?.();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusyId(""); }
  };

  if (!reqs || reqs.length === 0) return null;
  return (
    <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden mb-6" data-testid="sc-requests-panel">
      <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50/60 flex items-center gap-2">
        <Clock className="w-4 h-4 text-orange-500" strokeWidth={1.5} />
        <h3 className="font-display text-sm font-bold tracking-tight">{title}</h3>
      </div>
      <div className="divide-y divide-zinc-100 max-h-72 overflow-y-auto">
        {reqs.map((r) => (
          <div key={r.id} className="px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={`sc-req-row-${r.id}`}>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
              r.type === "descope" ? "bg-rose-100 text-rose-700" : "bg-sky-100 text-sky-700"}`}>
              {r.type === "descope" ? "De-scope" : "Re-scope"}
            </span>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-zinc-900 truncate">
                {r.line_title}
                <span className="text-zinc-400 font-normal"> · {r.upr_no || r.project_code}</span>
              </div>
              <div className="text-[11px] text-zinc-500">
                {r.type === "descope"
                  ? (r.full ? "Entire scope" : `Remove ${r.qty_change} ${r.unit}`)
                  : `Add ${r.qty_change} ${r.unit}`}
                {" · by "}{r.requested_by_name}
                {r.reason ? ` · ${r.reason}` : ""}
              </div>
            </div>
            <a href={r.email_url} target="_blank" rel="noreferrer" data-testid={`sc-email-link-${r.id}`}
               className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:underline">
              <Mail className="w-3 h-3" /> Email
            </a>
            {r.status === "pending" ? (
              canDecide ? (
                <div className="flex items-center gap-1.5">
                  <button onClick={() => decide(r.id, "approve")} disabled={busyId === r.id} data-testid={`sc-approve-${r.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md bg-emerald-600 text-white text-[11px] font-semibold hover:bg-emerald-700 disabled:opacity-40">
                    <Check className="w-3 h-3" /> Approve
                  </button>
                  <button onClick={() => decide(r.id, "reject")} disabled={busyId === r.id} data-testid={`sc-reject-${r.id}`}
                          className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-rose-300 text-rose-600 text-[11px] font-semibold hover:bg-rose-50 disabled:opacity-40">
                    <XCircle className="w-3 h-3" /> Reject
                  </button>
                </div>
              ) : (
                <span className="text-[11px] font-semibold text-amber-600">Awaiting approval</span>
              )
            ) : (
              <span className={`text-[11px] font-semibold ${r.status === "approved" ? "text-emerald-600" : "text-rose-500"}`}>
                {r.status === "approved" ? `Approved${r.applied ? ` — ${r.applied}` : ""}` : "Rejected"}
                {r.decided_by_name ? ` by ${r.decided_by_name}` : ""}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
